// boodaTimer — JS-side wrapper around the native BoodaTimer plugin.
//
// Drives the iOS Live Activity (Lock Screen card + Dynamic Island)
// for cook-mode timers. ONE activity per cook session, TIMER-CENTRIC:
// only steps with timers contribute milestones to the bar.
//
// API:
//   BoodaTimer.startSession({
//     clientId,
//     recipeName,
//     goalImageURL?,
//     timers: [{ id, stepIndex, stepTitle, label?, totalSeconds }],
//     initial: { state }
//   })
//   BoodaTimer.updateSession({ clientId, state })
//   BoodaTimer.endSession({ clientId, immediate? })
//   BoodaTimer.endAllSessions({ immediate? })
//
// `state` shape (mirrors Swift ContentState):
//   {
//     currentStepIndex: number,           // user's current recipe step
//     currentStepTitle: string,           // for headline when no active timer
//     activeTimerIndex: number,           // -1 = no active timer
//     activeTimerEndsAt: ISOstring | null,
//     activeTimerStartedAt: ISOstring | null,
//     isPaused: boolean,
//     pausedRemaining: number | null,     // seconds
//     completedTimerIndices: number[],    // indices into timers[]
//     helpers: Participant[],             // OTHER cooks active mid-cook
//     timerOwners: TimerOwner[],          // parallel to attributes.timers
//     lastTickAt: ISOstring | null,       // heartbeat — forces widget body re-render
//     pulseFrame: 0 | 1,                  // push-driven pulse counter
//     goalImageURL: string | null,        // recipe hero (push-updatable)
//   }
//
// On non-iOS, every method resolves to noop with { supported: false }.

import { Capacitor, registerPlugin } from "@capacitor/core";

const isIOSNative =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

const Native = registerPlugin("BoodaTimer");

const noop = () => Promise.resolve({ supported: false });

let supportPromise = null;

function isSupported() {
  if (!isIOSNative) return Promise.resolve({ supported: false });
  if (!supportPromise) supportPromise = Native.isSupported();
  return supportPromise;
}

function toIso(d) {
  if (d == null) return null;
  if (d instanceof Date) return d.toISOString();
  if (typeof d === "number" && Number.isFinite(d)) return new Date(d).toISOString();
  if (typeof d === "string") return d;
  return null;
}

function normalizeTimerOwner(o) {
  if (!o || typeof o !== "object") return null;
  const userId = String(o.userId ?? "");
  if (!userId) return null;
  const name = String(o.name ?? "");
  const colorHex = String(o.colorHex ?? "");
  if (!colorHex) return null;
  const out = { userId, name, colorHex };
  if (o.avatarURL) out.avatarURL = String(o.avatarURL);
  return out;
}

function normalizeHelper(h) {
  if (!h || typeof h !== "object") return null;
  const userId = String(h.userId ?? "");
  if (!userId) return null;
  const name = String(h.name ?? "");
  const out = {
    userId,
    name,
    currentStepIndex: Number.isFinite(Number(h.currentStepIndex))
      ? Math.max(0, Math.trunc(Number(h.currentStepIndex)))
      : 0,
    currentStepTitle: String(h.currentStepTitle ?? ""),
    activeTimerEndsAt: toIso(h.activeTimerEndsAt),
    activeTimerStartedAt: toIso(h.activeTimerStartedAt),
    isPaused: Boolean(h.isPaused),
    pausedRemaining:
      h.pausedRemaining != null && Number.isFinite(Number(h.pausedRemaining))
        ? Number(h.pausedRemaining)
        : null,
    // Flat-timer index — which segment of the lock-screen rail this
    // helper's CURRENT timer occupies. Distinct from currentStepIndex
    // (the recipe-step index): a multi-stage step has N flat-timer
    // segments but ONE stepIndex, so without this disambiguator the
    // widget couldn't tell two parallel stages apart and rendered
    // them at the same pct. -1 = unknown / caller didn't compute it
    // (widget falls back to stepIndex-only match).
    flatTimerIndex: Number.isFinite(Number(h.flatTimerIndex))
      ? Math.trunc(Number(h.flatTimerIndex))
      : -1,
  };
  return out;
}

function normalizeState(s) {
  if (!s) {
    return {
      currentStepIndex: 0,
      currentStepTitle: "",
      activeTimerIndex: -1,
      activeTimerEndsAt: null,
      activeTimerStartedAt: null,
      isPaused: false,
      pausedRemaining: null,
      completedTimerIndices: [],
      helpers: [],
      timerOwners: [],
      lastTickAt: null,
      pulseFrame: 0,
      goalImageURL: null,
    };
  }
  return {
    currentStepIndex: Number.isFinite(Number(s.currentStepIndex))
      ? Math.max(0, Math.trunc(Number(s.currentStepIndex)))
      : 0,
    currentStepTitle: String(s.currentStepTitle ?? ""),
    activeTimerIndex: Number.isFinite(Number(s.activeTimerIndex))
      ? Math.trunc(Number(s.activeTimerIndex))
      : -1,
    activeTimerEndsAt: toIso(s.activeTimerEndsAt),
    activeTimerStartedAt: toIso(s.activeTimerStartedAt),
    isPaused: Boolean(s.isPaused),
    pausedRemaining:
      s.pausedRemaining != null && Number.isFinite(Number(s.pausedRemaining))
        ? Number(s.pausedRemaining)
        : null,
    completedTimerIndices: Array.isArray(s.completedTimerIndices)
      ? s.completedTimerIndices
          .map((n) => Math.trunc(Number(n)))
          .filter((n) => Number.isFinite(n) && n >= 0)
      : [],
    helpers: Array.isArray(s.helpers)
      ? s.helpers.map(normalizeHelper).filter(Boolean)
      : [],
    timerOwners: Array.isArray(s.timerOwners)
      ? s.timerOwners.map(normalizeTimerOwner).filter(Boolean)
      : [],
    // Heartbeat — without this, every 1Hz push from CookMode is
    // Equatable-identical to the previous one and iOS skips the
    // widget body recompute. TickingAvatar's position then freezes
    // (it only re-derives from Date() on body re-render). The Swift
    // plugin's parseStateDict accepts ISO string OR Unix-seconds.
    lastTickAt: toIso(s.lastTickAt),
    // Push-driven pulse counter — flips 0 ↔ 1 each push. Widget
    // binds an animated halo (ring color + scale) to this value.
    pulseFrame: Number.isFinite(Number(s.pulseFrame))
      ? (Math.trunc(Number(s.pulseFrame)) & 1)
      : 0,
    // Recipe hero image — push-updatable (attributes are immutable
    // post-start, so an image that lands after the activity is
    // already running can only flow through here).
    goalImageURL: s.goalImageURL ? String(s.goalImageURL) : null,
  };
}

function normalizeTimer(t) {
  return {
    id: String(t?.id ?? ""),
    stepIndex: Math.trunc(Number(t?.stepIndex) || 0),
    stepTitle: String(t?.stepTitle ?? ""),
    label: String(t?.label ?? ""),
    totalSeconds: Math.max(0, Math.trunc(Number(t?.totalSeconds) || 0)),
    // Step-kind tag — sear / boil / bake / mix / prep / rest /
    // finish / default. Passed to the widget so the Dynamic Island
    // compact-leading slot can show an SF Symbol matching the
    // current cooking action.
    stepKind: typeof t?.stepKind === "string" && t.stepKind.length > 0
      ? t.stepKind
      : "default",
  };
}

function startSession({ clientId, recipeName, goalImageURL, timers, initial } = {}) {
  if (!isIOSNative) return noop();
  if (!clientId) {
    return Promise.reject(new Error("BoodaTimer.startSession: clientId required"));
  }
  const arr = Array.isArray(timers) ? timers.map(normalizeTimer) : [];
  return Native.startSession({
    clientId: String(clientId),
    recipeName: String(recipeName ?? ""),
    goalImageURL: goalImageURL ? String(goalImageURL) : undefined,
    timers: arr,
    initial: normalizeState(initial),
  });
}

function updateSession({ clientId, state } = {}) {
  if (!isIOSNative) return noop();
  if (!clientId) {
    return Promise.reject(new Error("BoodaTimer.updateSession: clientId required"));
  }
  return Native.updateSession({
    clientId: String(clientId),
    state: normalizeState(state),
  });
}

function endSession({ clientId, immediate = true } = {}) {
  if (!isIOSNative) return noop();
  if (!clientId) {
    return Promise.reject(new Error("BoodaTimer.endSession: clientId required"));
  }
  return Native.endSession({
    clientId: String(clientId),
    immediate: Boolean(immediate),
  });
}

function endAllSessions({ immediate = true } = {}) {
  if (!isIOSNative) return noop();
  return Native.endAllSessions({ immediate: Boolean(immediate) });
}

// endSessionsExcept({ keepClientIds, immediate }) — end every Live
// Activity on this device EXCEPT those whose clientId is in `keep`.
// Used by useTeamLiveActivity's once-per-process orphan sweep to
// dismiss stale cards from previous launches without nuking a fresh
// cook the lead just started.
function endSessionsExcept({ keepClientIds = [], immediate = true } = {}) {
  if (!isIOSNative) return noop();
  const keep = Array.isArray(keepClientIds)
    ? keepClientIds.map(String).filter(Boolean)
    : [];
  return Native.endSessionsExcept({
    keepClientIds: keep,
    immediate: Boolean(immediate),
  });
}

// cacheAvatar({ url, key }) — download an avatar PNG and write it to
// the App Group container at avatars/<key>.png. The widget extension
// reads this file synchronously when rendering segment avatars (no
// network required from inside the widget). Per Apple docs +
// community wisdom, AsyncImage in widget extensions hangs / fails
// silently on arbitrary network images; the App Group + local file
// pattern is the canonical workaround.
function cacheAvatar({ url, key } = {}) {
  if (!isIOSNative) return noop();
  if (!url || !key) {
    return Promise.reject(new Error("BoodaTimer.cacheAvatar: url + key required"));
  }
  return Native.cacheAvatar({ url: String(url), key: String(key) });
}

// cacheRecipeImage({ url, key }) — same App-Group write pattern as
// cacheAvatar, but for the recipe hero rendered in the Live Activity
// header. AsyncImage(url:) hangs in widget extensions, so we
// pre-download to the shared container and the widget reads
// recipe-heroes/<key>.png as a local file. Key is the URL's last
// path component without extension (the widget derives the same).
function cacheRecipeImage({ url, key } = {}) {
  if (!isIOSNative) return noop();
  if (!url || !key) {
    return Promise.reject(new Error("BoodaTimer.cacheRecipeImage: url + key required"));
  }
  return Native.cacheRecipeImage({ url: String(url), key: String(key) });
}

// focusActivity({ clientId }) — bump iOS Live Activity priority so this
// cook claims the Dynamic Island compact slot. Implementation re-pushes
// the activity's current ContentState with a refreshed lastTickAt and
// flipped pulseFrame — same content, fresh-enough timestamp that iOS
// re-orders the activities by recency and surfaces this one on the
// island. Used when the user explicitly switches between cooks in the
// multi-cook stack so the island reflects their current attention
// instead of whichever activity was most-recently auto-pushed.
//
// No-op on non-iOS-native. No-op when clientId isn't a managed activity
// (the native side guards that). Safe to fire-and-forget.
function focusActivity({ clientId } = {}) {
  if (!isIOSNative) return noop();
  if (!clientId) {
    return Promise.reject(new Error("BoodaTimer.focusActivity: clientId required"));
  }
  return Native.bumpActivityPriority({ clientId: String(clientId) });
}

// Derive the same cache key the widget uses — last path component
// without extension. Returns null when the URL isn't parseable or
// has no stem (defensive against query-string-only / hash-only URLs).
function recipeImageCacheKey(urlString) {
  if (!urlString || typeof urlString !== "string") return null;
  try {
    const u = new URL(urlString);
    const segments = u.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1] || "";
    const stem = last.replace(/\.[^.]+$/, "");
    return stem || null;
  } catch {
    return null;
  }
}

// Share-extension dead-drop pickup. The extension writes the shared
// URL into the App Group store because iOS 17+ routinely refuses to
// open the host app from a share extension — the deep link can't be
// trusted to arrive. App.jsx calls this on launch and on every
// foreground; the native side clears the slot on read (share imports
// exactly once). Resolves to the URL string or null.
function consumePendingShare() {
  if (!isIOSNative) return Promise.resolve(null);
  return Native.consumePendingShare()
    .then((r) => (typeof r?.url === "string" && r.url ? r.url : null))
    .catch(() => null);
}

export const BoodaTimer = {
  supported: isIOSNative,
  isSupported,
  consumePendingShare,
  startSession,
  updateSession,
  endSession,
  endAllSessions,
  endSessionsExcept,
  focusActivity,
  cacheAvatar,
  cacheRecipeImage,
  recipeImageCacheKey,
};

export default BoodaTimer;
